import { useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ items, index, onClose, onNav }) => {
  const item = items[index];

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNav(-1);
      if (e.key === "ArrowRight") onNav(1);
    },
    [onClose, onNav]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  return (
    <motion.div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo viewer: ${item.title}`}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-navy-dark/95 p-4 backdrop-blur-md sm:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        aria-label="Close photo viewer"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 rounded-full border border-white/20 p-2.5 text-white/80 transition-colors hover:border-gold hover:text-gold"
      >
        <X size={22} />
      </button>

      <button
        data-testid="lightbox-prev"
        aria-label="Previous photo"
        onClick={(e) => {
          e.stopPropagation();
          onNav(-1);
        }}
        className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white/80 transition-colors hover:border-gold hover:text-gold sm:left-6"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        data-testid="lightbox-next"
        aria-label="Next photo"
        onClick={(e) => {
          e.stopPropagation();
          onNav(1);
        }}
        className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-white/20 p-3 text-white/80 transition-colors hover:border-gold hover:text-gold sm:right-6"
      >
        <ChevronRight size={24} />
      </button>

      <motion.figure
        key={item.src}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="flex max-h-full flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={item.src}
          alt={item.title}
          className="max-h-[78vh] w-auto max-w-full rounded-xl object-contain shadow-2xl"
        />
        <figcaption className="mt-4 text-center">
          <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{item.cat}</span>
          <span className="mt-1 block text-sm font-semibold text-white/90">{item.title}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export default Lightbox;
